import { useParams, useLocation } from 'react-router-dom'
import { tools, categories } from '@/lib/tools-data'
import { useTranslation } from '@/hooks/use-translation'

interface SEOMetaProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  type?: string
}

export function SEOMeta({
  title: customTitle,
  description: customDescription,
  keywords: customKeywords,
  image = '/og-image.png',
  type = 'website'
}: SEOMetaProps) {
  const { toolId, categoryId } = useParams()
  const location = useLocation()
  const { t, language } = useTranslation()

  // Generate dynamic meta data based on current route
  const getMetaData = () => {
    let title = customTitle || t('app.title')
    let description = customDescription || t('app.subtitle')
    let keywords = customKeywords || 'retos diarios, juegos mentales, wordle, sudoku, puzzles, ejercicios cerebrales'

    // Game/Challenge page
    if (toolId) {
      const tool = tools.find(t => t.id === toolId)
      if (tool) {
        title = `${t(`tools.${tool.id}.name`)} - ${t('app.title')}`
        description = t(`tools.${tool.id}.description`)
        keywords = `${t(`tools.${tool.id}.name`)}, ${tool.tags.map(tag => t(`tags.${tag}`)).join(', ')}, retos diarios, juegos mentales`
      }
    }
    // Category page
    else if (categoryId) {
      const category = categories.find(c => c.id === categoryId)
      if (category) {
        title = `${t(`categories.${category.id}.name`)} - ${t('app.title')}`
        description = t(`categories.${category.id}.description`)
        keywords = `${t(`categories.${category.id}.name`)}, retos diarios, juegos mentales, ${categoryId}`
      }
    }

    return { title, description, keywords }
  }

  const { title, description, keywords } = getMetaData()
  const url = `https://retosdiarios.com${location.pathname}`

  return (
    <>
      {/* React 19 native meta tag support */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={language} />

      {/* Open Graph tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={`https://retosdiarios.com${image}`} />
      <meta property="og:site_name" content={t('app.title')} />
      <meta property="og:locale" content={language === 'es' ? 'es_ES' : language === 'en' ? 'en_US' : 'fr_FR'} />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`https://retosdiarios.com${image}`} />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Language alternates - English as default (no prefix) */}
      <link rel="alternate" hrefLang="en" href={`https://retosdiarios.com${location.pathname.replace(/^\/(en|es|fr)/, '')}`} />
      <link rel="alternate" hrefLang="es" href={`https://retosdiarios.com/es${location.pathname.replace(/^\/(en|es|fr)/, '')}`} />
      <link rel="alternate" hrefLang="fr" href={`https://retosdiarios.com/fr${location.pathname.replace(/^\/(en|es|fr)/, '')}`} />
      <link rel="alternate" hrefLang="x-default" href={`https://retosdiarios.com${location.pathname.replace(/^\/(en|es|fr)/, '')}`} />
    </>
  )
} 